import {
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import generatePDF from "react-to-pdf";
import {
  getInvoiceIssuerData,
  getInvoiceIssuerOptions,
  getInvoiceIssuers,
  getToken,
  getUser,
  requestErrorHandler,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import i18n from "../../../i18n";
import CTN_logo from "../../../images/ctnfi.png";

const server = EnvSettings.server;

const roundToTwo = (value) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return 0;
  }
  return Math.round((Number(value) + 1e-9) * 100) / 100;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return "";
  }
  return roundToTwo(value).toFixed(2);
};

const calculateDueDate = (days) => {
  const due = new Date();
  due.setDate(due.getDate() + days);
  return due.toISOString().slice(0, 10);
};

const getDefaultInvoiceDate = () => new Date().toISOString().slice(0, 10);

const getItemId = (item) =>
  item?.id || item?.sku || item?.name || crypto.randomUUID();

const buildDefaultEmailList = (order, clientInfo, emailSubs) => {
  const emails = [];
  const creatorEmail =
    order?.User?.emailAddress || order?.User?.email || getUser()?.email;
  if (creatorEmail) {
    emails.push(creatorEmail);
  }
  const clientEmail = clientInfo?.contactEmail;
  if (clientEmail) {
    emails.push(clientEmail);
  }
  // Add current editor
  emails.push(getUser()?.email);
  if (order?.EmailListOrders?.length) {
    order.EmailListOrders.forEach((entry) => {
      if (entry?.EmailList?.EmailListUsers?.length) {
        entry.EmailList.EmailListUsers.forEach((el) => {
          const email =
            el?.User?.emailAddress || el?.User?.email || el?.email || el;
          if (email) {
            emails.push(email);
          }
        });
      }
    });
  }
  if (emailSubs && emailSubs.length) {
    emailSubs.forEach((f) => {
      emails.push(f);
    });
  }
  return Array.from(new Set(emails)).join(", ");
};

const buildPaymentInstructions = (issuerInfo, t) => {
  const lines = [];
  if (!issuerInfo) {
    return lines;
  }
  const addLine = (label, value) => {
    if (value) {
      lines.push(label + " " + value);
    }
  };
  lines.push(t("orders.paymentInstructions"));
  addLine(t("orders.bankName") + ":", issuerInfo.bankName);
  addLine(t("orders.bankAddress") + ":", issuerInfo.bankAddress);
  addLine(t("orders.bankSwift") + ":", issuerInfo.bankSwift);
  addLine(t("orders.bankCode") + ":", issuerInfo.bankCode);
  addLine(
    t("orders.accountName") + ":",
    issuerInfo.accountName || issuerInfo.name,
  );
  addLine(t("orders.iban") + ":", issuerInfo.bankIban);
  lines.push(t("orders.countryOfOrigin"));
  lines.push("FINLAND");
  lines.push(t("orders.manufacturerConsigner"));
  lines.push("Cryotech Finland Oy");
  lines.push("Jaakonkatu 2, 01620 Vantaa, Finland");
  return lines;
};

const getPrepaymentValues = (data) => {
  if (!data) {
    return null;
  }
  const vatRate = Number(data.vat || 0);
  const vatMultiplier = 1 + vatRate / 100;
  const percentage = Number(data.depositPercentage);
  if (
    !isNaN(percentage) &&
    data.depositPercentage !== "" &&
    data.depositPercentage != null
  ) {
    const baseTotal = Number(data.totalPrice || 0);
    if (percentage >= 100) {
      return {
        exclVat: baseTotal,
        inclVat: baseTotal * vatMultiplier,
      };
    }
    const exclVat = (baseTotal * percentage) / 100;
    return {
      exclVat,
      inclVat: exclVat * vatMultiplier,
    };
  }
  return null;
};

const formatData = (showInvoice, emailSubs) => {
  if (!showInvoice) {
    return;
  }
  const order = showInvoice.Order ? showInvoice.Order : showInvoice;
  const clientInfo = order.clientInfo || order.clientInfoObject || {};
  const items =
    order.genericDevices?.map((item) => ({
      id: getItemId(item),
      sku: item.sku || "",
      name: item.name || "",
      count: item.count || 0,
      unit: item.unit || "",
      price: item.price ?? item.listPrice ?? 0,
      discount: item.discount || 0,
      hsCodes:
        item.PackingDetail?.hsCodes
          ?.map((code) => code?.code)
          .filter(Boolean) || [],
    })) || [];
  const subtotal = items.reduce((sum, item) => {
    const unitPrice =
      Number(item.price || 0) * (1 - Number(item.discount || 0) / 100);
    return sum + unitPrice * Number(item.count || 0);
  }, 0);
  const totalInclVat =
    Number(order.totalPrice ?? subtotal) * (1 + Number(order.vat ?? 0) / 100);

  return {
    issuer: order.invoiceIssuer || "finland",
    invoiceNo: order.number || "",
    invoiceDate: getDefaultInvoiceDate(),
    dueDate: calculateDueDate(7),
    lateInterest: "0,05% per day",
    invoiceType: order.metadata?.invoiceType || "proforma",
    invoiceLang: i18n.language,
    paymentStatus: order.paymentStatus,
    details: order.details || "",
    currency: order.currency || "EUR",
    vat: order.vat ?? 0,
    totalPrice: order.totalPrice ?? subtotal,
    depositAmount: "",
    depositPercentage: order.metadata?.depositPercentage ?? 100,
    emailList: buildDefaultEmailList(order, clientInfo, emailSubs),
    clientInfo: {
      companyName: clientInfo.companyName || "",
      companyAddress: clientInfo.companyAddress || "",
      country: clientInfo.country || clientInfo.location || "",
      contactName: clientInfo.contactName || "",
      contactEmail: clientInfo.contactEmail || "",
      contactPhone: clientInfo.contactPhone || "",
      companyVatId: clientInfo.companyVatId || "",
    },
    items,
    ...calculatePaidUnpaid(
      totalInclVat,
      order.paymentStatus,
      order.metadata?.depositPercentage,
      order.vat ?? 0,
    ),
  };
};

const calculateSubtotal = (items) =>
  items.reduce((sum, item) => {
    const unitPrice =
      Number(item.price || 0) * (1 - Number(item.discount || 0) / 100);
    return sum + unitPrice * Number(item.count || 0);
  }, 0);

const calculatePaidUnpaid = (
  totalInclVat,
  paymentStatus,
  depositPercentage,
  vat,
) => {
  const prepayment = getPrepaymentValues({
    totalPrice: totalInclVat,
    depositPercentage,
    vat,
  });
  const depositPaid =
    prepayment && !isNaN(prepayment.inclVat) ? prepayment.inclVat : 0;
  let paidAmount = 0;
  if (paymentStatus === "completed" || paymentStatus === "transit") {
    paidAmount = totalInclVat;
  } else if (paymentStatus === "deposit") {
    paidAmount = depositPaid;
  }
  const unpaidAmount = Math.max(
    0,
    Number(totalInclVat || 0) - Number(paidAmount || 0),
  );
  return {
    paidAmount: roundToTwo(paidAmount),
    unpaidAmount: roundToTwo(unpaidAmount),
  };
};

export default function CreateInvoice({
  showInvoice,
  setShowInvoice,
  callback,
  onFinish,
  emailSubs,
}) {
  const { t } = useTranslation();
  const componentRef = useRef();
  const [invoice, setInvoice] = useState();
  const [showPreview, setShowPreview] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [autoTotal, setAutoTotal] = useState(true);
  const [invoiceIssuers, setInvoiceIssuers] = useState([]);
  const dispatch = useDispatch();

  const downloadPdf = () =>
    generatePDF(
      () => {
        return componentRef.current;
      },
      {
        method: submit ? "build" : "open",
      },
    );

  const exit = (didSubmit = false) => {
    setShowInvoice(false);
    setInvoice();
    setShowPreview(false);
    setSubmit(false);
    if (onFinish) {
      onFinish(didSubmit);
    }
  };

  const updateInvoiceValue = (val, key, baseData) => {
    if (!key) {
      return;
    }
    const data = invoice ? { ...invoice } : { ...formatData(baseData) };
    if (key.startsWith("clientInfo.")) {
      const field = key.replace("clientInfo.", "");
      data.clientInfo = { ...data.clientInfo, [field]: val };
    } else if (key.startsWith("items.")) {
      const [, idx, field] = key.split(".");
      const items = data.items ? [...data.items] : [];
      if (items[idx]) {
        items[idx] = { ...items[idx], [field]: val };
        if (field === "hsCodes") {
          items[idx].hsCodes = String(val || "")
            .split("\n")
            .map((entry) => entry.trim())
            .filter(Boolean);
        }
        data.items = items;
        if (autoTotal) {
          data.totalPrice = calculateSubtotal(items);
          if (data.invoiceType === "commercial") {
            const totalInclVat =
              Number(data.totalPrice || 0) * (1 + Number(data.vat || 0) / 100);
            if (data.paidAmount !== "" && data.paidAmount != null) {
              data.unpaidAmount = Math.max(
                0,
                totalInclVat - Number(data.paidAmount || 0),
              );
            } else if (data.unpaidAmount !== "" && data.unpaidAmount != null) {
              data.paidAmount = Math.max(
                0,
                totalInclVat - Number(data.unpaidAmount || 0),
              );
            }
            data.paidAmount = roundToTwo(data.paidAmount);
            data.unpaidAmount = roundToTwo(data.unpaidAmount);
          }
        }
      }
    } else {
      data[key] = val;
      if (key === "totalPrice" && data.invoiceType === "commercial") {
        const totalInclVat =
          Number(data.totalPrice || 0) * (1 + Number(data.vat || 0) / 100);
        if (data.paidAmount !== "" && data.paidAmount != null) {
          data.unpaidAmount = Math.max(
            0,
            totalInclVat - Number(data.paidAmount || 0),
          );
        } else if (data.unpaidAmount !== "" && data.unpaidAmount != null) {
          data.paidAmount = Math.max(
            0,
            totalInclVat - Number(data.unpaidAmount || 0),
          );
        }
        data.paidAmount = roundToTwo(data.paidAmount);
        data.unpaidAmount = roundToTwo(data.unpaidAmount);
      }
      if (key === "paidAmount" && data.invoiceType === "commercial") {
        const totalInclVat =
          Number(data.totalPrice || 0) * (1 + Number(data.vat || 0) / 100);
        data.unpaidAmount = Math.max(0, totalInclVat - Number(val || 0));
        data.unpaidAmount = roundToTwo(data.unpaidAmount);
      }
      if (key === "unpaidAmount" && data.invoiceType === "commercial") {
        const totalInclVat =
          Number(data.totalPrice || 0) * (1 + Number(data.vat || 0) / 100);
        data.paidAmount = Math.max(0, totalInclVat - Number(val || 0));
        data.paidAmount = roundToTwo(data.paidAmount);
      }
      if (key === "invoiceType" && data.invoiceType === "commercial") {
        if (data.paidAmount == null || data.unpaidAmount == null) {
          const totalInclVat =
            Number(data.totalPrice || 0) * (1 + Number(data.vat || 0) / 100);
          const defaults = calculatePaidUnpaid(
            totalInclVat,
            data.paymentStatus,
            data.depositPercentage,
            data.vat,
          );
          data.paidAmount = defaults.paidAmount;
          data.unpaidAmount = defaults.unpaidAmount;
        }
      }
    }
    setInvoice({ ...data });
  };

  const submitOrder = (file) => {
    if (!file || !showInvoice) {
      return;
    }
    const data = new FormData();
    data.append("token", getToken());
    data.append("orderData", JSON.stringify(showInvoice));
    data.append("invoiceData", JSON.stringify(invoice));
    const pdfBlob = file.output("blob");
    const invoiceType = (invoice?.invoiceType || "invoice").toUpperCase();
    const invoiceNumber =
      invoice?.invoiceNo || showInvoice.number || crypto.randomUUID();
    const invoiceName = invoiceType + "_" + invoiceNumber + ".pdf";
    const pdfFile = new File([pdfBlob], invoiceName, {
      type: "application/pdf",
    });
    data.append("invoice", pdfFile);
    const requestOptions = {
      method: "POST",
      body: data,
    };
    fetch(server + "/add_invoice", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        setSubmit(false);
        if (callback) {
          callback();
        }
        exit(true);
      });
  };

  useEffect(() => {
    if (showInvoice) {
      setTimeout(() => {
        setInvoice(formatData(showInvoice, emailSubs));
        setAutoTotal(true);
      }, 100);
    }
  }, [showInvoice]);

  useEffect(() => {
    getInvoiceIssuers(setInvoiceIssuers, dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (showPreview && componentRef.current) {
      const currentLang = i18n.language;
      const targetLang = invoice?.invoiceLang;
      if (targetLang && targetLang !== currentLang) {
        i18n.changeLanguage(targetLang).then(() => {
          downloadPdf().then((pdf) => {
            if (submit) {
              submitOrder(pdf);
            }
            i18n.changeLanguage(currentLang);
            setShowPreview(false);
          });
        });
      } else {
        downloadPdf().then((pdf) => {
          if (submit) {
            submitOrder(pdf);
          }
          setShowPreview(false);
        });
      }
    }
    // eslint-disable-next-line
  }, [showPreview, submit]);

  const itemRows = useMemo(() => {
    if (!invoice?.items) {
      return [];
    }
    return invoice.items.map((item) => {
      const unitPrice =
        Number(item.price || 0) * (1 - Number(item.discount || 0) / 100);
      return {
        id: item.id || item.sku || item.name,
        name: item.name || "",
        count: item.count || 0,
        unit: item.unit || "",
        unitPrice,
        total: unitPrice * (item.count || 0),
        hsCodes: item.hsCodes || [],
      };
    });
  }, [invoice]);

  const issuerInfo = invoice?.issuer
    ? getInvoiceIssuerData(invoiceIssuers, invoice.issuer)
    : undefined;
  const vatAmount =
    Number(invoice?.totalPrice || 0) * (Number(invoice?.vat) / 100);
  const totalInclVat = Number(invoice?.totalPrice || 0) + vatAmount;
  const prepayment = getPrepaymentValues(invoice);
  const showPrepayment =
    invoice?.invoiceType === "proforma" &&
    prepayment &&
    !isNaN(prepayment.exclVat) &&
    prepayment.exclVat > 0;
  const paymentInstructions =
    invoice?.invoiceType === "proforma"
      ? buildPaymentInstructions(issuerInfo, t)
      : [];

  const primaryColor = "#003269";
  const lightBorder = "#c7d2e3";

  const rowStyle = {
    display: "flex",
    width: "100%",
  };

  const cellStyle = (width, align) => ({
    width,
    padding: "6px 6px",
    textAlign: align || "left",
    fontSize: 12,
    borderBottom: "1px solid " + lightBorder,
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
  });

  const headerCellStyle = (width, align) => ({
    width,
    padding: "6px 6px",
    textAlign: align || "left",
    fontSize: 12,
    color: "#ffffff",
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
  });

  const summaryRowStyle = (bold, highlight) => ({
    display: "flex",
    width: "100%",
    padding: "4px 0",
    backgroundColor: highlight ? "rgba(0,50,105,0.1)" : "transparent",
    fontWeight: bold ? "bold" : "normal",
    fontSize: 12,
    color: primaryColor,
  });

  return (
    <Dialog
      open={!!showInvoice}
      onClose={exit}
      aria-labelledby="create-invoice-dialog-title"
      aria-describedby="create-invoice-dialog-description"
      fullWidth
      maxWidth="xl"
    >
      <DialogTitle id="create-invoice-dialog-title">
        {t("create")} {t("orders.invoice")}?
      </DialogTitle>
      <DialogContent style={{ overflowY: "initial" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ borderBottom: "1px solid #000" }}>
            <Typography style={{ fontWeight: "bold" }}>
              {t("orders.invoiceDetails")}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <TextField
              label={t("orders.invoiceNumber")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.invoiceNo || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "invoiceNo", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label={t("date")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.invoiceDate || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "invoiceDate", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label={t("orders.dueDate")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.dueDate || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "dueDate", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label={t("orders.latePaymentInterest")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.lateInterest || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "lateInterest", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              select
              label={t("orders.invoiceIssuer")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.issuer || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "issuer", showInvoice);
              }}
            >
              {getInvoiceIssuerOptions(invoiceIssuers).map((issuer) => (
                <MenuItem key={issuer.name} value={issuer.name}>
                  {issuer.data?.name || issuer.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField
              select
              label={t("orders.invoiceType")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.invoiceType || "proforma"}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "invoiceType", showInvoice);
              }}
            >
              <MenuItem value="proforma">
                {t("orders.proformaInvoice")}
              </MenuItem>
              <MenuItem value="commercial">
                {t("orders.commercialInvoice")}
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={2}>
            <TextField
              label={t("orders.currency")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.currency || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "currency", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              label={t("orders.vat") + " %"}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.vat || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "vat", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("orders.subtotal")}
              variant="outlined"
              size="small"
              fullWidth
              disabled={autoTotal}
              value={invoice?.totalPrice ?? ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "totalPrice", showInvoice);
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoTotal}
                  onChange={(e) => {
                    const nextAuto = e.target.checked;
                    setAutoTotal(nextAuto);
                    if (nextAuto && invoice?.items) {
                      updateInvoiceValue(
                        calculateSubtotal(invoice.items),
                        "totalPrice",
                        showInvoice,
                      );
                    }
                  }}
                  color="primary"
                />
              }
              label={t("orders.autoSubtotalFromItems")}
            />
          </Grid>
          {invoice?.invoiceType === "commercial" ? (
            <>
              <Grid item xs={4}>
                <TextField
                  label={t("orders.paid")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={invoice?.paidAmount ?? ""}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "paidAmount",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={t("orders.unpaid")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={invoice?.unpaidAmount ?? ""}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "unpaidAmount",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
            </>
          ) : null}
          {invoice?.invoiceType === "proforma" ? (
            <>
              <Grid item xs={4}>
                <TextField
                  label={t("orders.prepaymentPercentage")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={invoice?.depositPercentage ?? ""}
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === "") {
                      updateInvoiceValue(
                        rawValue,
                        "depositPercentage",
                        showInvoice,
                      );
                      return;
                    }
                    const numericValue = Math.max(
                      0,
                      Math.min(100, Number(rawValue)),
                    );
                    updateInvoiceValue(
                      numericValue,
                      "depositPercentage",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12}>
            <TextField
              label={t("details")}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              value={invoice?.details || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "details", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={12} style={{ borderBottom: "1px solid #000" }}>
            <Typography style={{ fontWeight: "bold" }}>
              {t("orders.invoiceTo")}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("companyName")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.clientInfo?.companyName || ""}
              onChange={(e) => {
                updateInvoiceValue(
                  e.target.value,
                  "clientInfo.companyName",
                  showInvoice,
                );
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("country")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.clientInfo?.country || ""}
              onChange={(e) => {
                updateInvoiceValue(
                  e.target.value,
                  "clientInfo.country",
                  showInvoice,
                );
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("contactInfo.companyVatId")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.clientInfo?.companyVatId || ""}
              onChange={(e) => {
                updateInvoiceValue(
                  e.target.value,
                  "clientInfo.companyVatId",
                  showInvoice,
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("contactInfo.companyAddress")}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              value={invoice?.clientInfo?.companyAddress || ""}
              onChange={(e) => {
                updateInvoiceValue(
                  e.target.value,
                  "clientInfo.companyAddress",
                  showInvoice,
                );
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("contactInfo.contactName")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.clientInfo?.contactName || ""}
              onChange={(e) => {
                updateInvoiceValue(
                  e.target.value,
                  "clientInfo.contactName",
                  showInvoice,
                );
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("contactInfo.contactEmail")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.clientInfo?.contactEmail || ""}
              onChange={(e) => {
                updateInvoiceValue(
                  e.target.value,
                  "clientInfo.contactEmail",
                  showInvoice,
                );
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label={t("contactInfo.contactPhone")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.clientInfo?.contactPhone || ""}
              onChange={(e) => {
                updateInvoiceValue(
                  e.target.value,
                  "clientInfo.contactPhone",
                  showInvoice,
                );
              }}
            />
          </Grid>
          <Grid item xs={12} style={{ borderBottom: "1px solid #000" }}>
            <Typography style={{ fontWeight: "bold" }}>{t("items")}</Typography>
          </Grid>
          {invoice?.items?.map((item, idx) => (
            <Grid container item xs={12} spacing={1} key={item.id || idx}>
              <Grid item xs={3}>
                <TextField
                  label={t("description")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.name || ""}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "items." + idx + ".name",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  label={t("orders.quantity")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.count ?? ""}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "items." + idx + ".count",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  label={t("orders.unit")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.unit || ""}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "items." + idx + ".unit",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  label={t("orders.unitPrice")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.price ?? ""}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "items." + idx + ".price",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  label={t("orders.discount") + " %"}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={item.discount ?? ""}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "items." + idx + ".discount",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={t("orders.hsCode")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  value={(item.hsCodes || []).join("\n")}
                  onChange={(e) => {
                    updateInvoiceValue(
                      e.target.value,
                      "items." + idx + ".hsCodes",
                      showInvoice,
                    );
                  }}
                />
              </Grid>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} justify="flex-end" style={{ marginTop: 5 }}>
          <Grid
            item
            xs={12}
            style={{ borderBottom: "1px solid #000", marginBottom: 5 }}
          >
            <Typography style={{ fontWeight: "bold" }}>
              {t("orders.invoiceDelivery", "Invoice Delivery")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              label={t("orders.emailList")}
              variant="outlined"
              size="small"
              fullWidth
              helperText={t("orders.emailListHelper")}
              value={invoice?.emailList || ""}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "emailList", showInvoice);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              label={t("orders.invoiceLanguage")}
              variant="outlined"
              size="small"
              fullWidth
              value={invoice?.invoiceLang || i18n.language}
              onChange={(e) => {
                updateInvoiceValue(e.target.value, "invoiceLang", showInvoice);
              }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="fi">Suomi</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={exit} color="primary" variant="outlined" autoFocus>
          {t("no")}
        </Button>
        <Button
          onClick={() => {
            setShowPreview(true);
          }}
          color="primary"
          variant="contained"
        >
          {t("preview")}
        </Button>
        <Button
          onClick={() => {
            setSubmit(true);
            setShowPreview(true);
          }}
          color="primary"
          variant="contained"
        >
          {t("yes")}
        </Button>
      </DialogActions>
      {invoice ? (
        <div
          ref={componentRef}
          style={
            !showPreview
              ? { display: "none" }
              : {
                  position: "absolute",
                  top: 2000,
                  width: 900,
                  padding: 10,
                  paddingTop: 20,
                }
          }
        >
          <Container
            style={{
              fontFamily: "Helvetica, Arial, sans-serif",
              paddingTop: 30,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Typography
                style={{
                  fontSize: 25,
                  fontWeight: "bold",
                  color: primaryColor,
                  marginTop: 10,
                }}
              >
                {invoice?.invoiceType === "commercial"
                  ? t("orders.commercialInvoice")
                  : t("orders.proformaInvoice")}
              </Typography>
              <img
                alt="CTN logo"
                src={CTN_logo}
                style={{
                  height: 30,
                  marginBottom: 30,
                  filter:
                    "invert(1) sepia(0.1) saturate(1000) brightness(0.33) hue-rotate(150deg)",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div style={{ width: "55%" }}>
                <Typography
                  style={{
                    fontSize: 21,
                    color: primaryColor,
                    fontWeight: "bold",
                  }}
                >
                  {t("orders.invoiceTo")}:
                </Typography>
                <div style={{ marginTop: 8 }}>
                  <Typography
                    style={{ fontWeight: "bold", color: primaryColor }}
                  >
                    {invoice.clientInfo.companyName}
                  </Typography>
                  <Typography style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                    {invoice.clientInfo.companyAddress}
                  </Typography>
                  <Typography style={{ fontSize: 12 }}>
                    {invoice.clientInfo.country}
                  </Typography>
                  {invoice.clientInfo.contactName ? (
                    <Typography style={{ fontSize: 12 }}>
                      Contact: {invoice.clientInfo.contactName}
                    </Typography>
                  ) : null}
                  {invoice.clientInfo.contactEmail ? (
                    <Typography style={{ fontSize: 12 }}>
                      Email: {invoice.clientInfo.contactEmail}
                    </Typography>
                  ) : null}
                  {invoice.clientInfo.contactPhone ? (
                    <Typography style={{ fontSize: 12 }}>
                      Phone: {invoice.clientInfo.contactPhone}
                    </Typography>
                  ) : null}
                  {invoice.clientInfo.companyVatId ? (
                    <Typography style={{ fontSize: 12 }}>
                      VAT: {invoice.clientInfo.companyVatId}
                    </Typography>
                  ) : null}
                </div>
              </div>
              <div style={{ width: "40%" }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{ width: "100%" }}>
                    {[
                      {
                        label: t("orders.invoiceNumber"),
                        value: invoice.invoiceNo,
                      },
                      { label: t("date") + ":", value: invoice.invoiceDate },
                      invoice.invoiceType === "proforma"
                        ? {
                            label: t("orders.paymentTerm") + ":",
                            value:
                              t("orders.prepayment") +
                              " " +
                              (invoice.depositPercentage ?? 0) +
                              "%",
                          }
                        : null,
                      {
                        label: t("orders.dueDate") + ":",
                        value: invoice.dueDate,
                      },
                      {
                        label: t("orders.latePaymentInterest") + ":",
                        value: invoice.lateInterest,
                      },
                    ]
                      .filter(Boolean)
                      .map((row) => (
                        <div
                          key={row.label}
                          style={{ display: "flex", fontSize: 12, gap: 8 }}
                        >
                          <div
                            style={{
                              width: "30%",
                              textAlign: "left",
                              color: primaryColor,
                              fontWeight: "bold",
                            }}
                          >
                            {row.label}
                          </div>
                          <div style={{ width: "70%", textAlign: "right" }}>
                            {row.value}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                <div style={{ marginTop: 10, textAlign: "right" }}>
                  <Typography
                    style={{
                      fontWeight: "bold",
                      color: primaryColor,
                      fontSize: 12,
                    }}
                  >
                    {issuerInfo?.name || ""}
                  </Typography>
                  <Typography style={{ fontSize: 12 }}>
                    {issuerInfo?.street || ""}
                  </Typography>
                  <Typography style={{ fontSize: 12 }}>
                    {issuerInfo?.location || ""}
                  </Typography>
                  <Typography style={{ fontSize: 12 }}>
                    {issuerInfo?.vat || ""}
                  </Typography>
                </div>
              </div>
            </div>

            <div
              style={{
                borderTop: "2px solid " + primaryColor,
                marginTop: 16,
              }}
            />

            {invoice.details ? (
              <div style={{ marginTop: 8 }}>
                <Typography style={{ fontWeight: "bold", fontSize: 12 }}>
                  {t("details")}:
                </Typography>
                <Typography style={{ fontSize: 12 }}>
                  {invoice.details}
                </Typography>
              </div>
            ) : null}

            <div style={{ marginTop: 14 }}>
              <div
                style={{
                  ...rowStyle,
                  backgroundColor: primaryColor,
                }}
              >
                <div style={headerCellStyle("45%")}>{t("description")}</div>
                <div style={headerCellStyle("10%")}>{t("orders.quantity")}</div>
                <div style={headerCellStyle("10%")}>{t("orders.unit")}</div>
                <div style={headerCellStyle("15%", "right")}>
                  {t("orders.unitPrice")}
                </div>
                <div style={headerCellStyle("20%", "right")}>
                  {t("orders.totalExclVat")}
                </div>
              </div>
              {itemRows.map((row) => (
                <div key={row.id} style={rowStyle}>
                  <div style={cellStyle("45%")}>
                    <div>{row.name}</div>
                    {row.hsCodes.length ? (
                      <div style={{ fontSize: 9 }}>
                        {row.hsCodes
                          .map((code) =>
                            code ? t("orders.hsCode") + ": " + code : "",
                          )
                          .filter(Boolean)
                          .join("\n")}
                      </div>
                    ) : null}
                  </div>
                  <div style={cellStyle("10%")}>{row.count}</div>
                  <div style={cellStyle("10%")}>{row.unit}</div>
                  <div style={cellStyle("15%", "right")}>
                    {formatCurrency(row.unitPrice)}
                  </div>
                  <div style={cellStyle("20%", "right")}>
                    {formatCurrency(row.total)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ width: "35%", marginTop: 12 }}>
                <div style={summaryRowStyle(false, false)}>
                  <div style={{ width: "60%", textAlign: "right" }}>
                    {t("orders.subtotal")} 0%
                  </div>
                  <div
                    style={{ width: "40%", textAlign: "right", color: "#000" }}
                  >
                    {formatCurrency(invoice.totalPrice)}
                  </div>
                </div>
                <div style={summaryRowStyle(false, false)}>
                  <div style={{ width: "60%", textAlign: "right" }}>
                    {t("orders.vat")} {invoice.vat || 0}%
                  </div>
                  <div
                    style={{ width: "40%", textAlign: "right", color: "#000" }}
                  >
                    {formatCurrency(vatAmount)}
                  </div>
                </div>
                <div style={summaryRowStyle(true, true)}>
                  <div style={{ width: "60%", textAlign: "right" }}>
                    {t("orders.invoiceTotal")} ({invoice.currency})
                  </div>
                  <div
                    style={{ width: "40%", textAlign: "right", color: "#000" }}
                  >
                    {formatCurrency(totalInclVat)}
                  </div>
                </div>
                {invoice?.invoiceType === "commercial" ? (
                  <>
                    <div style={summaryRowStyle(false, false)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.paid")} ({invoice.currency})
                      </div>
                      <div
                        style={{
                          width: "40%",
                          textAlign: "right",
                          color: "#000",
                        }}
                      >
                        {formatCurrency(invoice.paidAmount)}
                      </div>
                    </div>
                    <div style={summaryRowStyle(true, true)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.unpaid")} ({invoice.currency})
                      </div>
                      <div
                        style={{
                          width: "40%",
                          textAlign: "right",
                          color: "#000",
                        }}
                      >
                        {formatCurrency(invoice.unpaidAmount)}
                      </div>
                    </div>
                  </>
                ) : null}
                {showPrepayment ? (
                  <>
                    <div style={summaryRowStyle(false, false)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.prepaymentWithoutVat")}
                      </div>
                      <div
                        style={{
                          width: "40%",
                          textAlign: "right",
                          color: "#000",
                        }}
                      >
                        {formatCurrency(prepayment.exclVat)}
                      </div>
                    </div>
                    <div style={summaryRowStyle(true, true)}>
                      <div style={{ width: "60%", textAlign: "right" }}>
                        {t("orders.prepaymentTotal")} ({invoice.currency})
                      </div>
                      <div
                        style={{
                          width: "40%",
                          textAlign: "right",
                          color: "#000",
                        }}
                      >
                        {formatCurrency(prepayment.inclVat)}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            {invoice?.invoiceType === "proforma" &&
            paymentInstructions.length ? (
              <div style={{ marginTop: 16, display: "flex" }}>
                <div style={{ width: "60%" }}>
                  {paymentInstructions.map((line, idx) => (
                    <Typography
                      key={idx}
                      style={{
                        fontSize: idx === 0 ? 11 : 10,
                        fontWeight: idx === 0 ? "bold" : "normal",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {line}
                    </Typography>
                  ))}
                </div>
              </div>
            ) : null}

            <div
              style={{
                borderTop: "1px solid " + lightBorder,
                marginTop: 18,
                paddingTop: 6,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ width: "60%" }}>
                  <Typography style={{ fontSize: 9 }}>
                    {issuerInfo?.companyPhone
                      ? t("orders.phone") + " " + issuerInfo.companyPhone
                      : ""}
                  </Typography>
                  <Typography style={{ fontSize: 9 }}>
                    {issuerInfo?.companyEmail
                      ? "   " +
                        t("orders.email") +
                        " " +
                        issuerInfo.companyEmail
                      : ""}
                  </Typography>
                  <Typography style={{ fontSize: 9 }}>
                    {issuerInfo?.companyWebsite
                      ? "   " +
                        t("orders.homepage") +
                        " " +
                        issuerInfo.companyWebsite
                      : ""}
                  </Typography>
                </div>
                <div style={{ width: "35%", textAlign: "right" }}>
                  <Typography style={{ fontSize: 9 }}>
                    {(issuerInfo?.bankName ? issuerInfo.bankName : "") +
                      (issuerInfo?.bankSwift
                        ? " " + t("orders.swift") + " " + issuerInfo.bankSwift
                        : "")}
                  </Typography>
                  <Typography style={{ fontSize: 9 }}>
                    {issuerInfo?.bankIban
                      ? t("orders.iban") + " " + issuerInfo.bankIban
                      : ""}
                  </Typography>
                </div>
              </div>
            </div>
          </Container>
        </div>
      ) : null}
    </Dialog>
  );
}
